import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import api from '../utils/api';
import {
  clearTokens,
  getAccessToken,
  setTokens,
} from '../utils/authTokenStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Reads from sessionStorage first, then falls back to localStorage (and pins
  // the value into sessionStorage so this tab keeps its own session even if
  // another tab logs in as a different user.
  const [token, setToken] = useState(getAccessToken());

  const fetchCurrentUser = async () => {
    try {
      // No cache-bust: the profile rarely changes and any explicit edit dispatches
      // `profile-updated`, which we listen for separately to force a re-fetch.
      const response = await api.get('/accounts/me/', { _skipCacheBust: true });
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const handleProfileUpdated = () => {
      if (token) fetchCurrentUser();
    };
    window.addEventListener('profile-updated', handleProfileUpdated);
    return () => window.removeEventListener('profile-updated', handleProfileUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/accounts/login/`, {
        email,
        password
      });
      const { access, refresh, user: userData } = response.data;
      setTokens({ access, refresh });
      setToken(access);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API_BASE_URL}/accounts/register/`, userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Registration failed'
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API_BASE_URL}/accounts/forgot-password/`, { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send reset link'
      };
    }
  };

  const resetPassword = async (uid, token, newPassword, newPasswordConfirm) => {
    try {
      await axios.post(`${API_BASE_URL}/accounts/reset-password/`, {
        uid,
        token,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password'
      };
    }
  };

  const logout = () => {
    // Clear both stores so a stale token cannot be inherited by new tabs.
    // Other already-open tabs keep their own sessionStorage and remain logged in
    // as whoever they are. This is the "per-tab session, last-login inheritance" model.
    clearTokens();
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasRole = (role) => {
    if (!user) return false;
    if (role === 'admin') return user.role === 'admin';
    if (role === 'coworker') return user.role === 'coworker' || user.role === 'admin';
    return true;
  };

  const value = {
    user,
    loading,
    login,
    register,
    forgotPassword,
    resetPassword,
    logout,
    isAuthenticated,
    hasRole,
    token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

